/* NephroCalculatorPanel — KDIGO 2024 nephrology calculators for the Doctor Portal.
 *
 * Educational clinical tool (NOT a medical device). Computes:
 *   • CKD-EPI 2021 eGFR (creatinine / cystatin / combined)
 *   • KDIGO CKD stage G1-G5 + albuminuria A1-A3
 *   • KFRE 2-year / 5-year risk
 *
 * All formulas are transparent, publicly-documented equations — no AI inference.
 * Falls under EU AI Act Art. 50 limited-risk / outside MDR scope.
 */
import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Activity, AlertTriangle, Droplets, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SEX_OPTIONS = [
  { v: "male",   l_bg: "Мъж",  l_en: "Male" },
  { v: "female", l_bg: "Жена", l_en: "Female" },
];

export default function NephroCalculatorPanel() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [age, setAge] = useState("");
  const [sex, setSex] = useState("male");
  const [cr, setCr] = useState("");
  const [cys, setCys] = useState("");
  const [acr, setAcr] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const compute = async () => {
    if (!age || (!cr && !cys)) {
      toast.error(bg ? "Въведете възраст + поне креатинин или цистатин C" : "Enter age + at least creatinine or cystatin C");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        age: Number(age),
        sex,
        creatinine_mg_dl: cr ? Number(cr) : null,
        cystatin_c_mg_L: cys ? Number(cys) : null,
        acr_mg_g: acr ? Number(acr) : null,
      };
      const { data } = await axios.post(`${API}/nephro/calculate`, payload);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка в калкулатора" : "Calculator error"));
    } finally {
      setLoading(false);
    }
  };

  const sevClass = (severity) => {
    if (severity === "attention") return isDark ? "bg-rose-500/20 text-rose-300 border-rose-700/50" : "bg-rose-100 text-rose-800 border-rose-300";
    if (severity === "monitor")   return isDark ? "bg-amber-500/20 text-amber-300 border-amber-700/50" : "bg-amber-100 text-amber-800 border-amber-300";
    return isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-700/50" : "bg-emerald-100 text-emerald-800 border-emerald-300";
  };

  return (
    <div className={`rounded-xl p-4 ${isDark ? "bg-white/5 border border-white/10" : "bg-white border border-gray-200"}`} data-testid="nephro-calculator-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
          <Droplets className="w-4 h-4 text-white" />
        </div>
        <div>
          <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Нефро калкулатор (KDIGO 2024)" : "Nephro calculator (KDIGO 2024)"}
          </h3>
          <p className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {bg ? "eGFR · CKD stage · KFRE прогноза" : "eGFR · CKD stage · KFRE risk"}
          </p>
        </div>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <label className="flex items-center gap-1.5 text-[11px]">
          <span className="opacity-70">{bg ? "Възраст" : "Age"}</span>
          <input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="65"
            className={`ml-auto w-16 px-1.5 py-1 rounded ${isDark ? "bg-white/5 border border-white/10 text-white" : "bg-gray-50 border border-gray-200"}`}
            data-testid="nephro-age-input" />
        </label>
        <label className="flex items-center gap-1.5 text-[11px]">
          <span className="opacity-70">{bg ? "Пол" : "Sex"}</span>
          <select value={sex} onChange={(e) => setSex(e.target.value)}
            className={`ml-auto px-1.5 py-1 rounded ${isDark ? "bg-white/5 border border-white/10 text-white" : "bg-gray-50 border border-gray-200"}`}
            data-testid="nephro-sex-input">
            {SEX_OPTIONS.map((o) => <option key={o.v} value={o.v}>{bg ? o.l_bg : o.l_en}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[11px] col-span-2">
          <span className="opacity-70">{bg ? "Креатинин (mg/dL)" : "Creatinine (mg/dL)"}</span>
          <input type="number" step="0.01" value={cr} onChange={(e) => setCr(e.target.value)} placeholder="1.2"
            className={`ml-auto w-20 px-1.5 py-1 rounded ${isDark ? "bg-white/5 border border-white/10 text-white" : "bg-gray-50 border border-gray-200"}`}
            data-testid="nephro-cr-input" />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] col-span-2">
          <span className="opacity-70">{bg ? "Цистатин C (mg/L) · опц." : "Cystatin C (mg/L) · opt."}</span>
          <input type="number" step="0.01" value={cys} onChange={(e) => setCys(e.target.value)} placeholder="1.0"
            className={`ml-auto w-20 px-1.5 py-1 rounded ${isDark ? "bg-white/5 border border-white/10 text-white" : "bg-gray-50 border border-gray-200"}`}
            data-testid="nephro-cys-input" />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] col-span-2">
          <span className="opacity-70">{bg ? "Албуминурия ACR (mg/g) · опц." : "Albuminuria ACR (mg/g) · opt."}</span>
          <input type="number" value={acr} onChange={(e) => setAcr(e.target.value)} placeholder="30"
            className={`ml-auto w-20 px-1.5 py-1 rounded ${isDark ? "bg-white/5 border border-white/10 text-white" : "bg-gray-50 border border-gray-200"}`}
            data-testid="nephro-acr-input" />
        </label>
      </div>

      <button onClick={compute} disabled={loading}
        className={`w-full py-1.5 rounded-lg text-[11px] font-semibold transition ${
          loading ? "opacity-50 cursor-not-allowed bg-gray-200 text-gray-500"
                  : "bg-gradient-to-r from-sky-500 to-cyan-500 text-white hover:from-sky-600 hover:to-cyan-600"}`}
        data-testid="nephro-calculate-btn">
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : (bg ? "Изчисли" : "Calculate")}
      </button>

      {/* Results */}
      {result && (
        <div className="space-y-2 mt-3" data-testid="nephro-results">
          {/* Primary eGFR card */}
          <div className={`rounded-lg p-3 border-2 ${sevClass(result.stage?.severity)}`} data-testid="nephro-primary-egfr">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold">
                <Activity className="w-3.5 h-3.5" />
                eGFR · {bg ? "Първичен резултат" : "Primary"}
              </div>
              <span className="text-[10px] font-bold opacity-70">{result.stage?.stage}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-extrabold">{result.primary_egfr}</span>
              <span className="text-[10px] opacity-70">mL/min/1.73m²</span>
            </div>
            <div className="text-[11px] font-semibold mt-0.5">{bg ? result.stage?.label_bg : result.stage?.label_en}</div>
          </div>

          {/* eGFR variants */}
          {(result.egfr_creatinine || result.egfr_cystatin || result.egfr_combined) && (
            <div className={`rounded-lg p-2 grid grid-cols-3 gap-1 text-center text-[10px] ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
              {result.egfr_creatinine != null && (
                <div>
                  <div className="opacity-60">{bg ? "Креатинин" : "Cr"}</div>
                  <div className="font-bold text-[12px]">{result.egfr_creatinine}</div>
                </div>
              )}
              {result.egfr_cystatin != null && (
                <div>
                  <div className="opacity-60">{bg ? "Цистатин" : "Cys"}</div>
                  <div className="font-bold text-[12px]">{result.egfr_cystatin}</div>
                </div>
              )}
              {result.egfr_combined != null && (
                <div>
                  <div className="opacity-60">{bg ? "Комбиниран" : "Combined"}</div>
                  <div className="font-bold text-[12px] text-sky-500">{result.egfr_combined}</div>
                </div>
              )}
            </div>
          )}

          {/* Albuminuria */}
          {result.albuminuria && (
            <div className={`rounded-lg p-2 ${isDark ? "bg-white/5" : "bg-gray-50"}`} data-testid="nephro-albuminuria">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] opacity-70">{bg ? "Албуминурия" : "Albuminuria"}</span>
                <span className="text-[11px] font-bold">{result.albuminuria.category}</span>
              </div>
              <div className="text-[10px] opacity-80">{bg ? result.albuminuria.label_bg : result.albuminuria.label_en}</div>
            </div>
          )}

          {/* KFRE */}
          {result.kfre && result.kfre.risk_2yr_pct != null && (
            <div className={`rounded-lg p-3 border ${isDark ? "bg-purple-950/25 border-purple-800/50" : "bg-purple-50 border-purple-200"}`} data-testid="nephro-kfre">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold mb-2 text-purple-500">
                <AlertTriangle className="w-3 h-3" />
                {bg ? "KFRE прогноза" : "KFRE risk"}
              </div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className={`text-xl font-extrabold ${isDark ? "text-purple-300" : "text-purple-700"}`}>{result.kfre.risk_2yr_pct}%</div>
                  <div className="text-[10px] opacity-70">{bg ? "2 години" : "2 years"}</div>
                </div>
                <div>
                  <div className={`text-xl font-extrabold ${isDark ? "text-purple-300" : "text-purple-700"}`}>{result.kfre.risk_5yr_pct}%</div>
                  <div className="text-[10px] opacity-70">{bg ? "5 години" : "5 years"}</div>
                </div>
              </div>
              <p className={`text-[9px] italic mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg ? "Tangri 4-var KFRE. Прогноза за нужда от заместителна бъбречна терапия (диализа/трансплантация)." : "Tangri 4-var KFRE. Probability of RRT (dialysis/transplant)."}
              </p>
            </div>
          )}

          <p className={`text-[10px] italic ${isDark ? "text-gray-500" : "text-gray-500"}`} data-testid="nephro-disclaimer">
            {bg
              ? "⚕️ Това е прозрачен публичен калкулатор (KDIGO 2024 формули). Не е медицинско изделие, не е клиничен съвет. Решенията остават на лекуващия нефролог."
              : "⚕️ Transparent public calculator (KDIGO 2024 formulas). Not a medical device, not clinical advice. Decisions remain with the prescribing nephrologist."}
          </p>
        </div>
      )}
    </div>
  );
}
